import React from 'react';

/**
 * Compact styled view of notification data, used in the bell dropdown and strategy alerts list.
 * Renders rule-type-specific pills based on the data payload.
 * Does NOT include raw JSON toggle — that lives in the full eval page.
 */

const fmtP = (v: number) => v >= 1000 ? v.toLocaleString(undefined, { maximumFractionDigits: 0 }) : v.toFixed(2);
const fmtVal = (v: number) => Math.abs(v) >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}M` : v.toLocaleString(undefined, { maximumFractionDigits: 0 });

export const NotificationDataView: React.FC<{ data: Record<string, any> | null }> = ({ data }) => {
  if (!data) return null;

  // ── Cost basis drift ──
  if (data.avgCost != null && data.driftPct != null) {
    const avgCost = Number(data.avgCost) || 0;
    const spot = Number(data.spotPrice) || 0;
    const drift = Number(data.driftPct) || 0;
    const absDrift = Math.abs(drift);
    const currency = data.baseCurrency || 'USD';
    const driftLabel = drift > 0 ? 'above' : 'below';
    const isDanger = absDrift > 20;
    const breakdown = data.breakdown as { onHand?: { qty: number; avgPrice: number; count: number }; purchaseOrders?: { qty: number; avgPrice: number; count: number }; hedges?: { qty: number; avgPrice: number; count: number } } | undefined;

    return (
      <div className="flex items-center gap-1 flex-wrap mt-1.5">
        <div className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-mono"
          style={{
            backgroundColor: drift > 0 ? '#fef2f2' : '#eff6ff',
            color: drift > 0 ? '#991b1b' : '#1e40af',
            border: `1px solid ${drift > 0 ? '#fca5a5' : '#93c5fd'}`,
            fontWeight: 700,
          }}>
          <span className="opacity-60 text-[7px] font-semibold">AVG</span>
          <span>{currency} {fmtP(avgCost)}</span>
        </div>
        <div className="flex items-center gap-0.5 px-1 py-0.5 rounded-full text-[8px] font-bold"
          style={{
            backgroundColor: isDanger ? '#fecaca' : '#fed7aa',
            color: isDanger ? '#991b1b' : '#9a3412',
          }}>
          {drift > 0 ? '↑' : '↓'}{absDrift.toFixed(0)}%
        </div>
        <div className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-mono"
          style={{ backgroundColor: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0' }}>
          <span className="opacity-60 text-[7px] font-semibold">SPOT</span>
          <span>{currency} {fmtP(spot)}</span>
        </div>
        {breakdown && (
          <>
            {breakdown.onHand && breakdown.onHand.qty > 0 && (
              <div className="px-1 py-0.5 rounded-full text-[8px] font-medium"
                style={{ backgroundColor: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0' }}>
                OH:{breakdown.onHand.qty.toLocaleString()}MT
              </div>
            )}
            {breakdown.purchaseOrders && breakdown.purchaseOrders.qty > 0 && (
              <div className="px-1 py-0.5 rounded-full text-[8px] font-medium"
                style={{ backgroundColor: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe' }}>
                PO:{breakdown.purchaseOrders.qty.toLocaleString()}MT
              </div>
            )}
            {breakdown.hedges && breakdown.hedges.qty > 0 && (
              <div className="px-1 py-0.5 rounded-full text-[8px] font-medium"
                style={{ backgroundColor: '#f5f3ff', color: '#5b21b6', border: '1px solid #ddd6fe' }}>
                H:{breakdown.hedges.qty.toLocaleString()}MT
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // ── Exposure threshold ──
  if (data.value != null && data.zone != null) {
    const val = Number(data.value);
    const zone = data.zone as string || '';
    let valColor = '#166534'; let valBg = '#f0fdf4'; let valBorder = '#86efac';
    if (zone.includes('danger')) { valColor = '#991b1b'; valBg = '#fef2f2'; valBorder = '#fca5a5'; }
    else if (zone.includes('warning')) { valColor = '#9a3412'; valBg = '#fff7ed'; valBorder = '#fdba74'; }

    return (
      <div className="flex items-center gap-1 flex-wrap mt-1.5">
        <div className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-mono"
          style={{ backgroundColor: valBg, color: valColor, border: `1px solid ${valBorder}`, fontWeight: 700 }}>
          <span className="opacity-60 text-[7px] font-semibold">EXPOSURE</span>
          <span>{fmtVal(val)}</span>
        </div>
        <div className="px-1 py-0.5 rounded-full text-[8px] font-bold"
          style={{
            backgroundColor: zone.includes('danger') ? '#fecaca' : zone.includes('warning') ? '#fed7aa' : '#d1fae5',
            color: zone.includes('danger') ? '#991b1b' : zone.includes('warning') ? '#9a3412' : '#065f46',
          }}>
          {zone.replace('_', ' ')}
        </div>
      </div>
    );
  }

  // ── Price target ──
  if (data.spotPrice != null && data.target != null) {
    const spot = Number(data.spotPrice) || 0;
    const target = Number(data.target) || 0;
    const currency = data.currency || '';
    const direction = data.direction || 'above';
    const triggered = (direction === 'above' && spot >= target) || (direction === 'below' && spot <= target);

    return (
      <div className="flex items-center gap-1 flex-wrap mt-1.5">
        <div className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-mono"
          style={{ backgroundColor: '#f0fdf4', color: '#166534', border: '1px solid #86efac' }}>
          <span className="opacity-60 text-[7px] font-semibold">TARGET</span>
          <span>{currency} {fmtP(target)}</span>
        </div>
        <span className="text-gray-400 text-[9px]">{direction === 'above' ? '≤' : '≥'}</span>
        <div className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-mono"
          style={{
            backgroundColor: triggered ? '#fef2f2' : '#f0fdf4',
            color: triggered ? '#991b1b' : '#166534',
            border: `1px solid ${triggered ? '#fca5a5' : '#86efac'}`,
            fontWeight: 700,
          }}>
          <span className="opacity-60 text-[7px] font-semibold">SPOT</span>
          <span>{currency} {fmtP(spot)}</span>
        </div>
      </div>
    );
  }

  // ── Month-based rules (buffer_breach, coverage_low, logistics_shortfall) ──
  if (data.positions?.months) {
    const months = data.positions.months as any[];
    const breachCount = months.filter((m: any) => m.coveragePct != null && m.coveragePct < 100).length;
    const totalMonths = data.condition?.months_ahead || months.length;

    return (
      <div className="flex items-center gap-1 flex-wrap mt-1.5">
        <div className="px-1.5 py-0.5 rounded-full text-[8px] font-bold"
          style={{
            backgroundColor: breachCount > 0 ? '#fecaca' : '#d1fae5',
            color: breachCount > 0 ? '#991b1b' : '#065f46',
          }}>
          {breachCount > 0 ? `${breachCount}/${totalMonths} months breached` : `${totalMonths} months OK`}
        </div>
      </div>
    );
  }

  return null;
};
